import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MasterTransaction } from '../types';
import { Link } from 'react-router-dom';
import { DollarSign, AlertCircle, CheckCircle2, Calendar, Clock, ShieldAlert, ArrowRightLeft } from 'lucide-react';
import { getSettings, TenantSettings } from '../services/settingsService';
import { useFirebase } from '../contexts/FirebaseContext';
import { useDashboardFilters } from '../contexts/DashboardFiltersContext';
import { getPendingAllocations } from '../services/allocationService';
import { getStaff } from '../services/staffService';
import { getPendingCashTips } from '../services/cashTipService';
import { getPendingJarTips } from '../services/jarTipService';
import { getPendingDisputes } from '../services/disputeService';
import { getRolePercentages, RolePercentage } from '../services/roleService';
import { isRejected, isPos, isCash, isJar, isDispute, isAllocated, isDashboardSubmittedPOS, isAllocatedPOS, isDashboardSubmittedCash, isAllocatedCash, isDashboardSubmittedJar, isAllocatedJar, isAllocatedOrPendingPOS, filterTransactionsByLocation } from '../lib/transactionUtils';
import { parseTransactionDate } from '../lib/utils';
import { formatCurrency } from '../lib/formatters';
import { useStoreLocation } from '../contexts/LocationContext';

export const DashboardSummary: React.FC = () => {
    const { dateRange, setDateRange, tipType, setTipType } = useDashboardFilters();
    const { tenantId, user, role } = useFirebase();
    const { activeLocationId } = useStoreLocation();
    const [allocated, setAllocated] = useState<MasterTransaction[]>([]);
    const [pending, setPending] = useState<MasterTransaction[]>([]);
    const [allTransactions, setAllTransactions] = useState<MasterTransaction[]>([]);
    const [currentUserStaff, setCurrentUserStaff] = useState<any>(null);
    const [currentUserRole, setCurrentUserRole] = useState<any>(null);
    const [settings, setSettings] = useState<TenantSettings | null>(null);
    const [pendingCash, setPendingCash] = useState<any[]>([]);
    const [pendingJar, setPendingJar] = useState<any[]>([]);
    const [pendingAllocations, setPendingAllocations] = useState<any[]>([]);
    const [pendingDisputesData, setPendingDisputesData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tenantId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Get settings first for timezone calculation
                const settingsData = await getSettings(tenantId);
                const timeZone = settingsData?.timezone || 'UTC';
                
                const getStartDateRangeInner = async (tz: string) => {
                    const formatter = new Intl.DateTimeFormat('en-CA', {
                        timeZone: tz,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    const [year, month, day] = formatter.format(new Date()).split('-').map(Number);
                    
                    const dEnd = new Date(year, month - 1, day);
                    const dStart = new Date(year, month - 1, day);

                    let startDay = '';
                    let endDay = '';

                    if (dateRange === 'last_closed_pay_period') {
                        // Try to get actual last closed pay period from payroll_exports
                        const exportsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
                        const q = query(exportsRef, where('tenantId', '==', tenantId), where('status', '==', 'Approved'));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            const sorted = snap.docs.map(doc => doc.data()).sort((a, b) => b.endDate.localeCompare(a.endDate));
                            startDay = sorted[0].startDate;
                            endDay = sorted[0].endDate;
                        } else {
                            dStart.setDate(dStart.getDate() - 14);
                            startDay = dStart.getFullYear() + '-' + String(dStart.getMonth() + 1).padStart(2, '0') + '-' + String(dStart.getDate()).padStart(2, '0');
                            endDay = dEnd.getFullYear() + '-' + String(dEnd.getMonth() + 1).padStart(2, '0') + '-' + String(dEnd.getDate()).padStart(2, '0');
                        }
                    } else if (dateRange === 'current_pay_period') {
                        // Try to get actual open pay period from payroll_periods
                        const periodsRef = collection(db, 'tenants', tenantId, 'payroll_periods');
                        const q = query(periodsRef, where('status', '==', 'Open'));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            const sorted = snap.docs.map(doc => doc.data()).sort((a, b) => b.startDate.localeCompare(a.startDate));
                            startDay = sorted[0].startDate;
                            endDay = sorted[0].endDate;
                        } else {
                            dStart.setDate(dStart.getDate() - 7);
                            startDay = dStart.getFullYear() + '-' + String(dStart.getMonth() + 1).padStart(2, '0') + '-' + String(dStart.getDate()).padStart(2, '0');
                            endDay = dEnd.getFullYear() + '-' + String(dEnd.getMonth() + 1).padStart(2, '0') + '-' + String(dEnd.getDate()).padStart(2, '0');
                        }
                    } else {
                        switch(dateRange) {
                            case 'today': break;
                            case 'last_30_days': dStart.setDate(dStart.getDate() - 30); break;
                            case 'month_to_date': dStart.setDate(1); break;
                            default: dStart.setDate(dStart.getDate() - 30);
                        }
                        startDay = dStart.getFullYear() + '-' + String(dStart.getMonth() + 1).padStart(2, '0') + '-' + String(dStart.getDate()).padStart(2, '0');
                        endDay = dEnd.getFullYear() + '-' + String(dEnd.getMonth() + 1).padStart(2, '0') + '-' + String(dEnd.getDate()).padStart(2, '0');
                    }
                    return { start: startDay, end: endDay };
                };
                
                const { start, end } = await getStartDateRangeInner(timeZone);

                // Get all master transactions to ensure parent lookups always succeed
                const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
                const snapshot = await getDocs(masterRef);
                
                const allData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
                
                // Filter transactions for the selected date range in-memory
                const dateFilteredData = allData.filter(t => {
                    const normalizedDate = parseTransactionDate(t.tipDate || t.timestamp);
                    return normalizedDate >= start && normalizedDate <= end;
                });
                
                const [staffData, rolePercentagesData, pendingCashRes, pendingJarRes, pendingDisputesRes, pendingAllocationsRes] = await Promise.all([
                    getStaff(tenantId),
                    getRolePercentages(tenantId),
                    getPendingCashTips(tenantId),
                    getPendingJarTips(tenantId),
                    getPendingDisputes(tenantId),
                    getPendingAllocations(tenantId)
                ]);
                
                // Filter pending actions by location
                const filterPendingByLocation = (items: any[]) => {
                    if (!activeLocationId || activeLocationId === 'all') return items;
                    return items.filter(item => {
                        if (item.locationId) return item.locationId === activeLocationId;
                        return activeLocationId === 'loc-downtown' || activeLocationId === tenantId;
                    });
                };
                
                setPendingCash(filterPendingByLocation(pendingCashRes));
                setPendingJar(filterPendingByLocation(pendingJarRes));
                setPendingDisputesData(filterPendingByLocation(pendingDisputesRes));
                setPendingAllocations(filterPendingByLocation(pendingAllocationsRes));
                
                const locationFilteredData = filterTransactionsByLocation(dateFilteredData, activeLocationId);
                const allocatedData = locationFilteredData.filter(t => isAllocatedOrPendingPOS(t, allData));
                const submittedData = locationFilteredData.filter(t => t.type === 'Submitted');
                const reportedData = locationFilteredData.filter(t => t.type === 'Reported');
                
                console.log('DEBUG: Fetched Tenant Transactions:', allData.length, 'Within range:', dateFilteredData.length, 'Location filtered:', locationFilteredData.length);
                
                setAllTransactions(allData);
                setAllocated(allocatedData);
                setPending([...submittedData, ...reportedData].filter(t => t.allocationStatus !== 'Finalized' && !isRejected(t)));
                setSettings(settingsData);

                if (user?.email) {
                    const myStaff = staffData.find(s => s.email === user.email);
                    const myRole = myStaff ? rolePercentagesData.find(rp => rp.role === myStaff.role) : null;
                    setCurrentUserStaff(myStaff || null);
                    setCurrentUserRole(myRole || null);
                }
            } catch (err) {
                console.error("Error fetching data in DashboardSummary:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, user?.email, dateRange, activeLocationId]);

    // Filtering logic based on tipType (incorporating parent lookup for SplitPool allocations)
    const filterByTipType = (t: MasterTransaction) => {
        if (tipType === 'total') return true;
        const isPosTip = isDashboardSubmittedPOS(t, allTransactions) || isAllocatedPOS(t, allTransactions);
        const isCashTip = isDashboardSubmittedCash(t, allTransactions) || isAllocatedCash(t, allTransactions);
        const isJarTip = isDashboardSubmittedJar(t, allTransactions) || isAllocatedJar(t, allTransactions);
        const isDisputeTip = isDispute(t, allTransactions); 
        
        if (tipType === 'pos' && isPosTip) return true;
        if (tipType === 'cash' && isCashTip) return true;
        if (tipType === 'jar' && isJarTip) return true;
        if (tipType === 'dispute' && isDisputeTip) return true;
        
        return false;
    };

    const filteredAllocated = allocated.filter(filterByTipType);
    const filteredPending = pending.filter(filterByTipType);

    // Reactive computation of myTips so it updates instantly when filters (tipType) change
    const myTips = useMemo(() => {
        if (!user?.email || !currentUserStaff) return 0;
        const isEligible = currentUserRole ? currentUserRole.isEligible !== false : false;
        if (!isEligible) return 0;

        const myTipsTxs = filteredAllocated.filter(t => {
            const byStaffId = currentUserStaff && t.staffId === currentUserStaff.id;
            const byEmail = t.email === user.email;
            const byName = currentUserStaff && t.staffName?.toLowerCase().trim() === currentUserStaff.name.toLowerCase().trim();
            
            return byStaffId || byEmail || byName;
        });
        
        return myTipsTxs.reduce((sum, t) => sum + (t.amount || 0), 0);
    }, [filteredAllocated, user, currentUserStaff, currentUserRole]);

    if (loading) return <div className="p-4 animate-pulse bg-gray-50 rounded-xl h-24"></div>;
    if (!tenantId) return <div className="p-4 text-red-500">Tenant ID missing.</div>;
    if (!settings) return (
        <div className="p-8 bg-amber-50 rounded-2xl border border-amber-100 text-amber-900 text-center">
            <h3 className="text-lg font-bold">Setup Required</h3>
            <p className="mt-2 text-sm">Your business settings have not been configured yet.</p>
            <Link to="/settings" className="mt-4 inline-block bg-amber-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-amber-700">
                Configure Business Settings
            </Link>
        </div>
    );

    // Calculations
    const isOwnerOrManager = role === 'admin' || role === 'manager' || user?.email?.toLowerCase() === 'admin@growezy.ai';
    
    const pendingDisputes = filteredPending.filter(t => isDispute(t, allTransactions));
    
    const pendingCashAmount = pendingCash.reduce((acc, t) => acc + (t.amount || 0), 0);
    const pendingJarAmount = pendingJar.reduce((acc, t) => acc + (t.jarAmount || 0), 0);
    const pendingDisputeAmount = pendingDisputesData.reduce((acc, t) => acc + (t.originalAmount || 0), 0);
    const pendingAllocationsAmount = pendingAllocations.reduce((acc, t) => acc + (t.amount || 0), 0);
    const totalPendingDisputes = pendingDisputes.reduce((acc, t) => acc + (t.amount || 0), 0);
    
    console.log('DEBUG: Pending Disputes:', pendingDisputesData);
    
    const totalAllocated = filteredAllocated.reduce((acc, t) => acc + (t.amount || 0), 0);
    
    let forecast = 0;
    if(settings.payCycle && settings.payDayDue) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const targetDayIndex = days.indexOf(settings.payDayDue);
        const settingsTimezone = settings?.timezone || 'UTC';
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: settingsTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const [year, month, day] = formatter.format(new Date()).split('-').map(Number);
        const today = new Date(year, month - 1, day);
        let diff = targetDayIndex - today.getDay();
        if (diff <= 0) diff += 7;
        
        const uniqueDates = new Set(filteredAllocated.map(t => t.tipDate));
        const avgDailyTips = filteredAllocated.length > 0 ? (totalAllocated / uniqueDates.size) : 0;
        forecast = avgDailyTips * diff;
    }

    const [y, m, d] = new Intl.DateTimeFormat('en-CA', {timeZone: settings?.timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date()).split('-').map(Number);
    const today = new Date(y, m - 1, d);
    
    const targetDayIndex = settings.payDayDue ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(settings.payDayDue) : -1;
    let daysUntilPayroll = 0;
    if (targetDayIndex !== -1) {
        daysUntilPayroll = targetDayIndex - today.getDay();
        if (daysUntilPayroll <= 0) daysUntilPayroll += 7;
    }

    const payrollUrgency = daysUntilPayroll <= 2 ? 'urgent' : daysUntilPayroll <= 5 ? 'warning' : 'normal';
    const getPayrollStyles = () => {
        switch (payrollUrgency) {
            case 'urgent': return "bg-red-50 border-red-100";
            case 'warning': return "bg-amber-50 border-amber-100";
            default: return "bg-indigo-50 border-indigo-100";
        }
    };
    const getPayrollTextColor = () => {
        switch (payrollUrgency) {
            case 'urgent': return "text-red-900";
            case 'warning': return "text-amber-900";
            default: return "text-indigo-900";
        }
    };
    const getPayrollIcon = () => {
        switch (payrollUrgency) {
            case 'urgent': return <AlertCircle className="text-red-500 w-6 h-6" />;
            case 'warning': return <Clock className="text-amber-500 w-6 h-6" />;
            default: return <Calendar className="text-indigo-500 w-6 h-6" />;
        }
    };

    console.log('DEBUG: isOwnerOrManager:', isOwnerOrManager);
    console.log('DEBUG: pendingAllocations.length:', pendingAllocations.length);
    console.log('DEBUG: pendingDisputesData.length:', pendingDisputesData.length);
    console.log('DEBUG: pendingCash.length:', pendingCash.length);
    console.log('DEBUG: pendingJar.length:', pendingJar.length);

    return (
        <div className="space-y-6">
            {isOwnerOrManager && (pendingAllocations.length > 0 || pendingDisputesData.length > 0 || pendingCash.length > 0 || pendingJar.length > 0) && (
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-indigo-600" />
                        Needs Review & Approval
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {pendingAllocations.length > 0 && (
                            <Link to="/allocations?tab=review" className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center justify-between hover:border-amber-200 transition-all">
                                <div>
                                    <h3 className="text-xs font-black text-amber-900 uppercase tracking-widest">Pending Allocation</h3>
                                    <p className="text-xl font-black text-amber-950">{formatCurrency(pendingAllocationsAmount)}</p>
                                    <p className="text-[10px] text-amber-700 font-bold uppercase">{pendingAllocations.length} submissions</p>
                                </div>
                                <ArrowRightLeft className="text-amber-500 w-6 h-6" />
                            </Link>
                        )}
                        {pendingDisputesData.length > 0 && (
                            <Link to="/disputes?tab=review" className="p-4 bg-red-50 rounded-xl border border-red-100 flex items-center justify-between hover:border-red-200 transition-all">
                                <div>
                                    <h3 className="text-xs font-black text-red-900 uppercase tracking-widest">Pending Disputes</h3>
                                    <p className="text-xl font-black text-red-950">{formatCurrency(pendingDisputeAmount)}</p>
                                    <p className="text-[10px] text-red-700 font-bold uppercase">{pendingDisputesData.length} disputes</p>
                                </div>
                                <ShieldAlert className="text-red-500 w-6 h-6" />
                            </Link>
                        )}
                        {pendingCash.length > 0 && (
                            <a href="/tips?tab=review" className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-between hover:border-blue-200 transition-all">
                                <div>
                                    <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest">Pending Cash</h3>
                                    <p className="text-xl font-black text-blue-950">{formatCurrency(pendingCashAmount)}</p>
                                    <p className="text-[10px] text-blue-700 font-bold uppercase">{pendingCash.length} submissions</p>
                                </div>
                                <DollarSign className="text-blue-500 w-6 h-6" />
                            </a>
                        )}
                        {pendingJar.length > 0 && (
                            <a href="/tips?tab=review" className="p-4 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between hover:border-purple-200 transition-all">
                                <div>
                                    <h3 className="text-xs font-black text-purple-900 uppercase tracking-widest">Pending Jar</h3>
                                    <p className="text-xl font-black text-purple-950">{formatCurrency(pendingJarAmount)}</p>
                                    <p className="text-[10px] text-purple-700 font-bold uppercase">{pendingJar.length} submissions</p>
                                </div>
                                <DollarSign className="text-purple-500 w-6 h-6" />
                            </a>
                        )}
                    </div>
                </div>
            )}
            
            <div className="flex flex-wrap justify-between items-end mb-4 gap-4">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1">
                    {dateRange.replace(/_/g, ' ')} • {tipType.toUpperCase()} TIPS
                </div>
                <div className="flex gap-2">
                    <select 
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value as any)}
                        className="text-[10px] bg-slate-100 p-1 rounded border-none font-bold text-slate-600 uppercase"
                    >
                        <option value="current_pay_period">Current Pay Period</option>
                        <option value="last_closed_pay_period">Last Closed Pay Period</option>
                        <option value="month_to_date">Month-to-Date</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="today">Today</option>
                    </select>
                    <select 
                        value={tipType}
                        onChange={(e) => setTipType(e.target.value as any)}
                        className="text-[10px] bg-slate-100 p-1 rounded border-none font-bold text-slate-600 uppercase"
                    >
                        <option value="total">Total Tips</option>
                        <option value="cash">Cash Tips</option>
                        <option value="pos">POS Tips</option>
                        <option value="jar">Jar Tips</option>
                        <option value="dispute">Disputes</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">

                {/* Avg Tips per Shift */}
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-between min-w-0">
                    <div className="min-w-0">
                        <h3 className="text-[9px] font-black text-emerald-900 uppercase tracking-widest truncate">Avg Per Shift</h3>
                        <p className="text-xs sm:text-sm md:text-base lg:text-lg font-black text-emerald-950 mt-0.5 md:mt-1 truncate" title={formatCurrency(filteredAllocated.length > 0 ? (totalAllocated / filteredAllocated.length) : 0)}>
                            {formatCurrency(filteredAllocated.length > 0 ? (totalAllocated / filteredAllocated.length) : 0)}
                        </p>
                    </div>
                    <CheckCircle2 className="text-emerald-500 w-4 h-4 sm:w-5 sm:h-5 shrink-0 ml-1" />
                </div>
    
                {/* Next Payroll Forecast */}
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-between min-w-0">
                    <div className="min-w-0">
                        <h3 className="text-[9px] font-black text-blue-900 uppercase tracking-widest truncate">Forecast</h3>
                        <p className="text-xs sm:text-sm md:text-base lg:text-lg font-black text-blue-950 mt-0.5 md:mt-1 truncate" title={formatCurrency(forecast)}>{formatCurrency(forecast)}</p>
                    </div>
                    <DollarSign className="text-blue-500 w-4 h-4 sm:w-5 sm:h-5 shrink-0 ml-1" />
                </div>
    
                {/* Payroll Due */}
                <div className={`p-3 rounded-xl border flex items-center justify-between min-w-0 ${getPayrollStyles()}`}>
                    <div className="min-w-0">
                        <h3 className={`text-[9px] font-black uppercase tracking-widest truncate ${getPayrollTextColor()}`}>Payroll Due</h3>
                        <p className="text-xs sm:text-sm md:text-base lg:text-lg font-black text-slate-950 mt-0.5 md:mt-1 truncate">{daysUntilPayroll} days</p>
                    </div>
                    <div className="shrink-0 ml-1">{getPayrollIcon()}</div>
                </div>
            </div>
        </div>
    );
};
